import type { PersistStorage, StorageValue } from 'zustand/middleware';

/**
 * Secure encrypted storage for Zustand persist middleware
 * Uses AES-GCM encryption via Web Crypto API
 *
 * In production:
 * - Data is encrypted before being stored in localStorage
 * - Encryption key is derived from a device fingerprint + app secret
 * - Data cannot be read directly from browser devtools
 * - Session data is cleared when the browser tab is closed (more secure for pro accounts)
 */

const ENCRYPTION_ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

// App-specific secret (should ideally come from env in a real scenario)
// This gets bundled into the code, making reverse engineering harder
const APP_SECRET = 'volovolo-pro-secure-2024';

/**
 * Generate a device-specific identifier
 * This makes the encryption key unique per browser/device
 */
function getDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
  ];
  return components.join('|');
}

/**
 * Derive an encryption key from the device fingerprint and app secret
 */
async function deriveKey(): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = encoder.encode(getDeviceFingerprint() + APP_SECRET);

  // Create a key from the raw material
  const baseKey = await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  // Derive an AES-GCM key
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('volovolo-salt'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt data using AES-GCM
 */
async function encrypt(data: string): Promise<string> {
  try {
    const key = await deriveKey();
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    const encryptedData = await crypto.subtle.encrypt(
      { name: ENCRYPTION_ALGORITHM, iv },
      key,
      encoder.encode(data)
    );

    // Combine IV and encrypted data, then base64 encode
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('[SecureStorage] Encryption failed:', error);
    throw error;
  }
}

/**
 * Decrypt data using AES-GCM
 */
async function decrypt(encryptedData: string): Promise<string> {
  try {
    const key = await deriveKey();
    const decoder = new TextDecoder();

    // Decode base64 and extract IV and data
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    const iv = combined.slice(0, IV_LENGTH);
    const data = combined.slice(IV_LENGTH);

    const decryptedData = await crypto.subtle.decrypt(
      { name: ENCRYPTION_ALGORITHM, iv },
      key,
      data
    );

    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('[SecureStorage] Decryption failed:', error);
    throw error;
  }
}

/**
 * Check if we're in production mode
 */
const isProduction = import.meta.env.PROD;

/**
 * Synchronous cache for encrypted storage operations
 * Since Web Crypto API is async, we need to handle this carefully
 */
const storageCache = new Map<string, StorageValue<unknown>>();

/**
 * Promise with timeout helper
 */
function withTimeout<T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMessage)), ms)
    ),
  ]);
}

// Timeout for crypto operations (5 seconds)
const CRYPTO_TIMEOUT_MS = 5000;

/**
 * Initialize cache from localStorage on load
 */
async function initializeCache<T>(name: string): Promise<StorageValue<T> | null> {
  const encrypted = localStorage.getItem(name);
  if (encrypted) {
    try {
      // Add timeout to prevent infinite hang on slow crypto operations
      const decrypted = await withTimeout(
        decrypt(encrypted),
        CRYPTO_TIMEOUT_MS,
        'Decryption timeout'
      );
      const parsed = JSON.parse(decrypted) as StorageValue<T>;
      storageCache.set(name, parsed);
      return parsed;
    } catch (error) {
      // If decryption fails or times out, clear the data
      console.warn('[SecureStorage] Failed to decrypt, clearing data:', error);
      localStorage.removeItem(name);
    }
  }
  return null;
}

/**
 * Create a secure storage adapter for Zustand persist middleware
 *
 * In development: Uses plain localStorage for easy debugging
 * In production: Encrypts all data with AES-GCM
 */
export function createSecureStorage<T>(_storageName: string): PersistStorage<T> {
  return {
    getItem: async (name: string): Promise<StorageValue<T> | null> => {
      if (!isProduction) {
        const item = localStorage.getItem(name);
        return item ? JSON.parse(item) : null;
      }

      // Check cache first
      const cached = storageCache.get(name);
      if (cached) {
        return cached as StorageValue<T>;
      }

      // Decrypt and return the data
      const encrypted = localStorage.getItem(name);
      if (encrypted) {
        const decrypted = await initializeCache<T>(name);
        return decrypted;
      }

      return null;
    },

    setItem: (name: string, value: StorageValue<T>): void => {
      if (!isProduction) {
        localStorage.setItem(name, JSON.stringify(value));
        return;
      }

      // Update cache immediately
      storageCache.set(name, value);

      // Encrypt and save asynchronously
      encrypt(JSON.stringify(value))
        .then((encrypted) => {
          localStorage.setItem(name, encrypted);
        })
        .catch((error) => {
          console.error('[SecureStorage] Failed to save:', error);
        });
    },

    removeItem: (name: string): void => {
      storageCache.delete(name);
      localStorage.removeItem(name);
    },
  };
}

/**
 * Disable React DevTools and Zustand devtools in production
 * This prevents state inspection via browser extensions
 */
export function disableDevToolsInProduction(): void {
  if (!isProduction) return;

  // Disable React DevTools
  if (typeof window !== 'undefined') {
    // @ts-expect-error - React DevTools hook
    if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      // @ts-expect-error - React DevTools hook
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function() {};
    }
  }
}

/**
 * Clear all secure storage (useful for logout)
 */
export function clearSecureStorage(): void {
  console.log('[SecureStorage] Clearing storage...');
  console.log('[SecureStorage] localStorage keys before:', Object.keys(localStorage));

  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('volovolo') || key.includes('storage'))) {
      keysToRemove.push(key);
    }
  }

  console.log('[SecureStorage] Keys to remove:', keysToRemove);

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    storageCache.delete(key);
  });

  // Also clear the in-memory cache completely
  storageCache.clear();

  console.log('[SecureStorage] localStorage keys after:', Object.keys(localStorage));
  console.log('[SecureStorage] Cache cleared, size:', storageCache.size);
}

/**
 * Export a no-op devtools middleware for production
 * This prevents state from being visible in Redux DevTools
 */
export const devtoolsConfig = isProduction
  ? { enabled: false }
  : { enabled: true, name: 'Volovolo Pro' };
